'use client';

import { config } from '@cfg';
import CardGridWithPagination from '@components/CardGridWithPagination';
import PropertyListingsMap from '@components/PropertyListingsMap/PropertyListingsMap';
import { useGlobalSiteHeaderContext } from '@components/GlobalSiteHeader/GlobalSiteHeaderContext';
import ElasticAutocomplete from '@components/Hero/SearchHero/ElasticAutocomplete';
import { Block } from '@components/Layout';
import ListCount from '@components/ListCount';
import NullResultsSearchListings from '@components/NullResults/SearchListings';
import { Pagination } from '@components/Pagination';
import { SelectedFilters } from '@components/PropertySearchFilters';
import Select from '@components/Select';
import { useSnackbar } from '@components/Snackbar';
import {
  ListingFilters,
  ListingSearchResult,
  LocalLogicCountry,
  Prediction,
  PredictionAddressComponents,
  PredictionTypeEnum,
  SortDir,
} from '@engelvoelkersamericas/api';
import auth from '@util/api/auth';
import { LIST_REQUEST_PAGE_SIZE, MAP_REQUEST_PAGE_SIZE } from '@util/constants';
import { useAction } from '@util/customHooks/useAction';
import { objectToQueryParams } from '@util/queryParams/objectToQueryParams';
import { queryParamsToObject } from '@util/queryParams/queryParamsToObject';
import { LifestyleParam, SelectOption } from '@util/types';
import { apiClient } from 'api-client';
import { AxiosResponse } from 'axios';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { FC, useEffect, useMemo, useState } from 'react';
import PropertyCard from './PropertyCard';
import PropertySearchFilters, { EvaListSortKey, SortMode, ViewMode } from './PropertySearchFilters';
import {
  DropdownFilterWrapper,
  Headline,
  Main,
  MapSection,
  OverWriteSelect,
  PaginationContainer,
  PropertiesContainer,
  PropertiesSubheaderContainer,
  PropertiesSubheaderContainerCol,
  PropertyActions,
  PropertyListWrapper,
  PropertyListWrapperInner,
  PropertyWrapper,
  SearchbarWrapper,
  SearchInputWrapper,
  SearchMain,
  SearchPropertiesWrapper,
} from './styles';
import {
  convertListingFiltersToQueryParams,
  convertQueryParamsToListingFilters,
  defaultListingFilters,
  setListingQueryParams,
} from './utils';
import { useShop } from '@util/customHooks/useShop';
import { ShopNavigationOptions } from '@data/shops';

interface PropertiesSearchProps {
  locale: string;
  shopIds: string | null;
  /** If true, only shows listings provided by E&V */
  evListingsOnly: boolean;
  googleSearchCountryCodeList?: string[] | null;
  lifestyle: LifestyleParam | null;
  disableCurrentLocation: boolean;
  shopNavigationOptions?: ShopNavigationOptions;
  hideRentFilters?: boolean;
  shopGroupId?: string;
}

export interface Center {
  lat: string;
  lng: string;
}

const PropertiesSearch: FC<PropertiesSearchProps> = ({
  evListingsOnly,
  locale,
  shopIds,
  googleSearchCountryCodeList,
  disableCurrentLocation,
  shopNavigationOptions,
  hideRentFilters = false,
  lifestyle,
  shopGroupId,
}) => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { showSnackbar } = useSnackbar();
  const { shop } = useShop();
  const { placesSearchObj, toSetModalSection } = useGlobalSiteHeaderContext();

  const [paramsLoaded, setParamsLoaded] = useState(false);
  const [sortMode, setSortMode] = useState<SortMode>('priceDesc');
  const [page, setPage] = useState<number>(1);
  const [filters, setFilters] = useState<ListingFilters>({});
  const [neighborhoodId, setNeighborhoodId] = useState<string | null>(null);
  const [country, setCountry] = useState<string | null>(null);

  const [location, setLocation] = useState('');
  const [userPolygons, setUserPolygons] = useState<{ lat: number; lng: number }[][] | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [zoomValue, setZoomValue] = useState<string | null>(null);
  const [center, setCenter] = useState<Center | null>(null);

  const [saveSearchStatus, setSaveSearchStatus] = useState({
    isEnable: true,
    isAlreadySaved: false,
  });

  const { start, data: listingData, pending } = useAction<ListingSearchResult>();

  const params = new URLSearchParams(searchParams.toString());
  // Set the initial query params
  useEffect(() => {
    const page = params.get('currentPage');
    const sort = params.get('sort');
    const view = params.get('view');
    const location = params.get('location');
    const zoom = params.get('zoom');
    const lat = params.get('lat');
    const lng = params.get('lng');
    const neighborhoodId = params.get('neighborhoodId');
    const country = params.get('country');

    if (page) setPage(parseInt(page));
    if (sort) setSortMode(sort as SortMode);
    if (view) setViewMode(view as ViewMode);
    if (location) setLocation(location);
    if (neighborhoodId) setNeighborhoodId(neighborhoodId);
    if (country) setCountry(country);
    if (zoom && lat && lng) {
      setZoomValue(zoom);
      setCenter({ lat, lng });
    }

    const filters = convertQueryParamsToListingFilters(searchParams, lifestyle);
    setFilters(filters);

    setParamsLoaded(true);
    getSavedSearches();
  }, []);

  // Retrieve listings whenever pagination or filters change
  // Also update the query params in the URL
  const newQueryParams = convertListingFiltersToQueryParams(filters, lifestyle);
  useEffect(() => {
    if (!paramsLoaded || !placesSearchObj) return;

    newQueryParams.append('currentPage', page.toString());
    newQueryParams.append('sort', sortMode);
    newQueryParams.append('view', viewMode);

    if (location) {
      newQueryParams.append('location', location);
    }

    // Need to keep this here so values persist with other filters
    if (zoomValue && center) {
      newQueryParams.append('zoom', zoomValue);
      newQueryParams.append('lat', center.lat);
      newQueryParams.append('lng', center.lng);
    }

    setListingQueryParams(router, pathname, newQueryParams);

    getListings();
  }, [
    paramsLoaded,
    placesSearchObj,
    page,
    sortMode,
    viewMode,
    location,
    neighborhoodId,
    country,
    filters,
    lifestyle,
    userPolygons,
  ]);

  useEffect(() => {
    // Updating these separate from other url params allows for a smoother map experience
    if (zoomValue && center) {
      newQueryParams.append('zoom', zoomValue);
      newQueryParams.append('lat', center.lat);
      newQueryParams.append('lng', center.lng);
    }

    setListingQueryParams(router, pathname, newQueryParams, searchParams);
  }, [zoomValue, center]);

  useEffect(() => {
    // Updates save search status whenever search params change
    checkIsAlreadySavedSearch();
  }, [searchParams]);

  const checkIsAlreadySavedSearch = () => {
    // Check if this search is already saved
    const storedSearchList = localStorage.getItem('saveSearch');
    const savedSearchList = storedSearchList ? JSON.parse(storedSearchList) : null;
    const selectedFilters: SelectedFilters = queryParamsToObject(searchParams.toString());

    // Remove page and view from the saved search
    delete selectedFilters.currentPage;
    delete selectedFilters.view;
    const searchUrl = `/${pathname.split('/').slice(2).join('/')}?${objectToQueryParams(
      selectedFilters,
    )}`;
    const isAlreadySaved =
      savedSearchList &&
      savedSearchList.length > 0 &&
      savedSearchList.some((item) => item == searchUrl);
    setSaveSearchStatus({ isEnable: !isAlreadySaved, isAlreadySaved });
  };

  const getSavedSearches = async () => {
    const userData = await auth.getUser();
    if (userData?.attributes?.sub) {
      const res = await apiClient.getSavedSearches({ userId: userData.attributes.sub });
      let array: string[] = [];
      if (res?.data && res.data.length) {
        res.data.map((data) => {
          const parseData = JSON.parse(data?.Search);
          array.push(parseData?.url);
        });
      }
      localStorage.setItem('saveSearch', JSON.stringify(array));

      checkIsAlreadySavedSearch();
    }
  };

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handleSortChange = (sortMode: SortMode) => {
    setSortMode(sortMode);
    setPage(1);
  };

  const handleViewChange = (viewMode: ViewMode) => {
    setViewMode(viewMode);
    setPage(1);
  };

  const handleFiltersChange = (filters?: ListingFilters) => {
    setFilters({ ...defaultListingFilters, ...filters });
    setPage(1);
  };

  const getListings = async () => {
    const isMapRequest = viewMode !== 'list';
    const pageSize = isMapRequest ? MAP_REQUEST_PAGE_SIZE : LIST_REQUEST_PAGE_SIZE;
    const listingSortKey = EvaListSortKey[sortMode];
    const listingSortDir = sortMode.endsWith('Desc') ? SortDir.Desc : SortDir.Asc;

    const finalFilters: ListingFilters = {
      ...filters,
      evOnly: evListingsOnly,
      shopIds: shopIds ? shopIds.split(',') : undefined,
    };

    // If there is a location, attempt to get the polygon from the S3 polygonsearch file first
    let searchPolygon: { lat: number; lng: number }[] | undefined;
    if (location && placesSearchObj?.length) {
      const matchedPlace = placesSearchObj.find((place) => {
        const address = Object.keys(place)?.[0];
        return address?.trim() === location.trim();
      });
      if (matchedPlace) {
        const address = Object.keys(matchedPlace)?.[0];
        if (address) {
          searchPolygon = matchedPlace[address].polygon;
        }
      }
    }

    // Apply user drawn polygon first if available
    if (userPolygons && userPolygons.length > 0) {
      finalFilters.geoPolygons = userPolygons.map((polygon) => [
        polygon.map((p) => [p.lng, p.lat]),
      ]);
    }
    // Then use the hardcoded polygon from the S3 file if one was found
    else if (searchPolygon) {
      finalFilters.geoPolygons = [[searchPolygon.map((p) => [p.lng, p.lat])]];
    }

    await start({
      action: async () => {
        let res: AxiosResponse<ListingSearchResult, any>;

        if (neighborhoodId) {
          res = await apiClient.getListingsByNeighborhood({
            id: neighborhoodId,
            page,
            size: pageSize,
            sortKey: listingSortKey,
            sortDir: listingSortDir,
            listingFilters: finalFilters,
            country: country ?? 'other',
            locale: locale === 'fr-CA' ? 'fr' : locale,
          });
        } else {
          res = await apiClient.getListings({
            page,
            size: pageSize,
            sortKey: listingSortKey,
            sortDir: listingSortDir,
            listingFilters: finalFilters,
          });
        }

        // Adjust duplicate coordinates before returning listings
        const seen = new Map();
        res.data.listings?.forEach((listing) => {
          if (!listing.Latitude || !listing.Longitude) {
            return;
          }

          // Create a unique key for each lat-lng pair
          let key = `${listing.Latitude},${listing.Longitude}`;

          // Check if the lat-lng pair has already been seen
          while (seen.has(key)) {
            // If duplicate, adjust the lat and lng slightly
            listing.Latitude += 0.00002; // Adjust by a very small increment
            listing.Longitude += 0.00002; // Adjust by a very small increment
            // Update the key to check again
            key = `${listing.Latitude},${listing.Longitude}`;
          }

          // Mark this lat-lng pair as seen
          seen.set(key, true);
        });

        return res?.data;
      },
    });
  };

  const handleSaveSearchLog = async (prediction: Prediction) => {
    const normalizedSearch: Partial<Prediction> = prediction;
    delete normalizedSearch['count'];
    await apiClient.saveFrequentlySearchedArea({
      saveFrequentlySearchedAreaDto: {
        searchTerm: JSON.stringify(normalizedSearch),
        locationType: prediction.type as {},
        shopOrShopGroupId: shopGroupId ? shopGroupId : shopIds ? shopIds.split(',')[0] : 'americas',
      },
    });
  };

  const handleSearch = async (prediction: Prediction) => {
    if (
      prediction.type !== PredictionTypeEnum.Address &&
      prediction.type !== PredictionTypeEnum.PostalCode
    ) {
      await handleSaveSearchLog(prediction);
    }

    // Clear at the beginning of search so it doesn't use previous values
    setNeighborhoodId('');
    setCountry('');

    if (prediction.listingUrl && !prediction.listingUrl.includes('properties')) {
      router.push(prediction.listingUrl);
      return;
    }

    const localLogicId = prediction?.addressComponents?.localLogicId;
    const localLogicCountry =
      prediction?.addressComponents?.country === 'United States'
        ? LocalLogicCountry.Usa
        : LocalLogicCountry.Canada;

    if (localLogicId && localLogicCountry) {
      setNeighborhoodId(localLogicId);
      setCountry(localLogicCountry);
    }

    const localLogicAddressComponents: PredictionAddressComponents = {
      localLogicId: localLogicId,
      country: localLogicCountry,
    };

    const formattedAddressComponents: PredictionAddressComponents = localLogicId
      ? localLogicAddressComponents
      : prediction.addressComponents;

    // Update filters with the new location
    setFilters((prev) => {
      delete prev.country;
      delete prev.stateOrProvince;
      delete prev.city;
      delete prev.cityRegion;
      delete prev.neighborhood;
      delete prev.postalCode;
      delete prev.streetName;
      delete prev.streetNumber;
      delete prev.neighborhoodId;

      return {
        ...prev,
        ...formattedAddressComponents,
        location: prediction.text,
      };
    });

    const query: Record<string, string> = {
      ...formattedAddressComponents,
      location: prediction.text,
    };

    const newParams = new URLSearchParams(query);

    // Update query param with the current geocode location
    setListingQueryParams(router, pathname, newParams, searchParams);

    // Trigger a new search by resetting location
    setLocation(prediction.text);
  };

  const handleClear = () => {
    setFilters((prev) => {
      delete prev.country;
      delete prev.stateOrProvince;
      delete prev.city;
      delete prev.cityRegion;
      delete prev.neighborhood;
      delete prev.postalCode;
      delete prev.streetName;
      delete prev.streetNumber;
      delete prev.neighborhoodId;

      return {
        ...prev,
        location: '',
      };
    });
    setLocation('');
  };

  const onSavedSearchClick = async () => {
    if (!saveSearchStatus.isAlreadySaved) {
      try {
        const userData = await auth.getUser();
        if (userData?.attributes?.sub) {
          const queryString = searchParams.toString();
          if (queryString) {
            setSaveSearchStatus({ ...saveSearchStatus, isAlreadySaved: true });
            const selectedFilters: SelectedFilters = queryParamsToObject(queryString);

            // Remove page and view from the saved search
            delete selectedFilters.currentPage;
            delete selectedFilters.view;

            const searchUrl = `/${pathname.split('/').slice(2).join('/')}?${objectToQueryParams(
              selectedFilters,
            )}`;
            const response = await apiClient.saveSearch({
              userId: userData?.attributes?.sub,
              createUserSavedSearchDto: {
                search: JSON.stringify({ ...selectedFilters, url: searchUrl }),
              },
            });
            if (typeof response === 'object') {
              showSnackbar('Search saved successfully!', 'success');
            } else {
              showSnackbar('Search is already saved!', 'warning');
            }
          }
        } else {
          toSetModalSection('accountIntro');
        }
      } catch (err) {
        toSetModalSection('accountIntro');
      }
    }
  };

  const headerTitle = useMemo(() => {
    const shortLocationTitleForHeader =
      searchParams.get('locationtitle') || searchParams.get('location') || '';
    const listingTypes = searchParams.get('listingTypes')?.split(',');

    let listingTypeTitle: string | undefined;
    if (!listingTypes || (listingTypes.includes('For Sale') && listingTypes.includes('For Rent'))) {
      listingTypeTitle = t('Properties for Sale and Rent');
    } else if (listingTypes.includes('For Sale')) {
      listingTypeTitle = t('Properties for Sale');
    } else if (listingTypes.includes('For Rent')) {
      listingTypeTitle = t('Properties for Rent');
    }

    // Always show geo information in the header if available
    let title = shortLocationTitleForHeader || shop?.name || '';

    // Add lifestyle after location if available
    if (lifestyle?.length) {
      title = lifestyle ? `${lifestyle[0]!.toUpperCase()}${lifestyle.slice(1)}` : '';
    }

    return `${title} ${listingTypeTitle}`;
  }, [searchParams, lifestyle]);

  const sortOptions: SelectOption<SortMode>[] = useMemo(() => {
    const options: SelectOption<SortMode>[] = [
      { title: t('Price (Low to High)'), value: 'priceAsc' },
      { title: t('Price (High to Low)'), value: 'priceDesc' },
      { title: t('Date (Newest - Oldest)'), value: 'onMarketDateDesc' },
      { title: t('Date (Oldest - Newest)'), value: 'onMarketDateAsc' },
    ];

    // Only show this option if we are not filtering out non-E&V listings
    if (!evListingsOnly) {
      options.unshift({
        title: t('Engel & Völkers Listings'),
        value: 'listings',
      });
    }

    return options;
  }, [evListingsOnly]);

  const viewOptions: { title: string; value: ViewMode }[] = useMemo(() => {
    return [
      { title: t('List'), value: 'list' },
      { title: t('Map'), value: 'map' },
      { title: t('50/50'), value: '50/50' },
    ];
  }, []);

  const listingCards = useMemo(() => {
    if (!listingData) return [];

    return listingData.listings.map((listing, idx) => {
      return (
        <PropertyCard
          dataTestId={`property-listing-card-${idx}`}
          idx={idx}
          key={listing.ListingKey}
          listing={listing}
        />
      );
    });
  }, [listingData]);

  return (
    <Main id="search-main-wrapper">
      <SearchMain>
        <SearchbarWrapper id="search-sticky">
          <ElasticAutocomplete
            id="property-search-input"
            locale={locale}
            initialValue={searchParams.get('location')}
            placeholder={t('City, Neighborhood, Address or ZIP')}
            evListingsOnly={evListingsOnly}
            countries={googleSearchCountryCodeList}
            disableCurrentLocation={disableCurrentLocation}
            showTabs={false}
            onPredictionClick={handleSearch}
            onClear={handleClear}
          />
          <PropertyWrapper>
            <PropertySearchFilters
              currentFilters={filters}
              onApply={handleFiltersChange}
              shopNavigationOptions={shopNavigationOptions ?? {}}
              hideRentFilters={hideRentFilters}
              lifestyle={lifestyle}
              onSavedSearchClick={onSavedSearchClick}
              saveSearchStatus={saveSearchStatus}
            />
          </PropertyWrapper>
        </SearchbarWrapper>
      </SearchMain>

      <SearchPropertiesWrapper $viewBy={viewMode}>
        <PropertyListWrapper>
          <PropertyListWrapperInner $viewBy={viewMode}>
            <PropertyActions>
              <Headline data-testid="properties-search-header">{headerTitle}</Headline>

              <PropertiesSubheaderContainer>
                <PropertiesSubheaderContainerCol>
                  <ListCount
                    pageSize={listingData?.pageSize}
                    currentPage={listingData?.currentPage}
                    totalRecords={listingData?.totalRecords}
                    type={t('Properties')}
                  />
                  <DropdownFilterWrapper>
                    {viewMode != 'map' && (
                      <>
                        <OverWriteSelect>
                          <Select
                            options={sortOptions}
                            selectedItem={{
                              value: sortMode,
                              title: sortOptions.find((o) => o.value === sortMode)!.title,
                            }}
                            onSelect={({ value }) => {
                              handleSortChange(value as SortMode);
                            }}
                            title={t('Sort by') + ':'}
                            label={t('Sort by') + ':'}
                            isSort
                          />
                        </OverWriteSelect>
                      </>
                    )}
                    <Select
                      options={viewOptions}
                      selectedItem={viewOptions.find((o) => o.value === viewMode)}
                      title={t('View by') + ':'}
                      onSelect={({ value }) => {
                        handleViewChange(value as ViewMode);
                      }}
                      isSort
                    />
                  </DropdownFilterWrapper>
                </PropertiesSubheaderContainerCol>
              </PropertiesSubheaderContainer>
            </PropertyActions>
          </PropertyListWrapperInner>

          {listingData && (
            <>
              {viewMode !== 'map' && (
                <PropertiesContainer>
                  <CardGridWithPagination
                    cardsPerPage={LIST_REQUEST_PAGE_SIZE}
                    cardArr={listingCards}
                    cardType="property"
                    testIdType="property-listing"
                    pagination={false}
                  />
                  {listingData?.totalPages > 1 && (
                    <PaginationContainer>
                      <Pagination
                        currentPage={listingData.currentPage}
                        totalPages={listingData.totalPages}
                        pageSelected={handlePageChange}
                      />
                    </PaginationContainer>
                  )}
                </PropertiesContainer>
              )}
              {listingData.listings.length === 0 && !pending && viewMode !== 'map' && (
                <Block>
                  <NullResultsSearchListings
                    isSVG={true}
                    header={t('noResultsHeader')}
                    bodyText={t('noResultsContactShopOrAdvisor')}
                    findAdvisorUrl={
                      shopIds ? '/our-advisors' : '/shops-and-advisors/advisor-locator'
                    }
                    onBeginNewSearchBtnClicked={handleClear}
                    isShopPage={shop && shop.name}
                    newSearchUrl={shop && shop.name ? '/our-neighborhoods' : '/properties'}
                  />
                </Block>
              )}
            </>
          )}
        </PropertyListWrapper>

        {listingData?.listings[0] &&
          listingData.listings[0].Latitude &&
          (viewMode === 'map' || viewMode === '50/50') && (
            <MapSection>
              <PropertyListingsMap
                listings={listingData.listings ?? []}
                mapPropertyLoader={false}
                config={config}
                setZoomValue={setZoomValue}
                setCenter={setCenter}
                center={center}
                zoomValue={zoomValue ? Number(zoomValue) : location ? 9 : 6}
                onPolygonChange={(polygon) => setUserPolygons(polygon ? [polygon] : null)}
              />
            </MapSection>
          )}
      </SearchPropertiesWrapper>
    </Main>
  );
};

export default PropertiesSearch;
